/**
 * OfflineRecapDialog — "While You Were Away" modal shown on login
 * when ships were damaged or destroyed while the player was offline.
 * Created in MMOHUDScene (fixed camera) so input works correctly.
 */

import Phaser from 'phaser';
import {
  Colors, CSSColors, Fonts, FontSize, Space, Radius, createDialogChrome,
} from './GameUITheme';
import { EventBus } from '../../utils/EventBus';
import type { OfflineRecapEvent } from '../../managers/MMOStateManager';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';
const ROW_HEIGHT = 86;
const HEADER_H = 72;
const FOOTER_H = 56;
const SCROLL_PADDING = 12;
const MAX_VISIBLE_ROWS = 5;
const BTN_W = 110;
const BTN_H = 32;

export class OfflineRecapDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private scrollContainer: Phaser.GameObjects.Container;
  private scrollMask!: Phaser.Display.Masks.GeometryMask;
  private backdrop: Phaser.GameObjects.Graphics;
  private backdropZone: Phaser.GameObjects.Zone;
  private maskGraphics: Phaser.GameObjects.Graphics;
  private scrollY = 0;
  private maxScrollY = 0;
  private contentHeight = 0;

  constructor(scene: Phaser.Scene, events: OfflineRecapEvent[]) {
    const cam = scene.cameras.main;
    const mobile = isMobile(scene);
    const dialogW = mobile ? 340 : 520;

    const contentRows = Math.min(events.length, MAX_VISIBLE_ROWS);
    const dialogH = HEADER_H + (contentRows * ROW_HEIGHT) + SCROLL_PADDING * 2 + FOOTER_H;

    const cx = Math.round((cam.width - dialogW) / 2);
    const cy = Math.round((cam.height - dialogH) / 2);
    super(scene, cx, cy);
    this.dw = dialogW;
    scene.add.existing(this);
    this.setDepth(250);

    // Dark backdrop
    this.backdrop = scene.add.graphics();
    this.backdrop.setDepth(249);
    this.backdrop.fillStyle(0x000000, 0.6);
    this.backdrop.fillRect(0, 0, cam.width, cam.height);
    this.backdropZone = scene.add.zone(0, 0, cam.width, cam.height).setOrigin(0, 0);
    this.backdropZone.setDepth(249);
    this.backdropZone.setInteractive();

    const close = () => this.destroy();

    createDialogChrome(scene, this, dialogW, dialogH, {
      onClose: close,
      title: I18n.t('dialog.while_away'),
    });

    // Summary line
    const destroyed = events.filter(e => e.type === 'destroyed').length;
    const damaged = events.filter(e => e.type === 'damaged').length;
    const summaryParts: string[] = [];
    if (destroyed > 0) summaryParts.push(`${destroyed} ship${destroyed > 1 ? 's' : ''} destroyed`);
    if (damaged > 0) summaryParts.push(`${damaged} ship${damaged > 1 ? 's' : ''} damaged`);
    const summaryText = summaryParts.join(', ') || 'Activity detected';

    const summary = scene.add.text(Space.lg, HEADER_H - 12, summaryText, {
      fontSize: FontSize.sm,
      color: destroyed > 0 ? CSSColors.red : CSSColors.gold,
      fontFamily: Fonts.body,
    });
    this.add(summary);

    // Scrollable event list
    const scrollTop = HEADER_H + 4;
    this.scrollContainer = scene.add.container(0, scrollTop);
    this.add(this.scrollContainer);

    this.buildEventRows(events);

    // Scroll mask
    const visibleHeight = contentRows * ROW_HEIGHT + SCROLL_PADDING * 2;
    this.maskGraphics = scene.make.graphics({});
    this.maskGraphics.fillRect(cx, cy + scrollTop, dialogW, visibleHeight);
    this.scrollMask = new Phaser.Display.Masks.GeometryMask(scene, this.maskGraphics);
    this.scrollContainer.setMask(this.scrollMask);

    this.maxScrollY = Math.max(0, this.contentHeight - visibleHeight);

    // Wheel scrolling on the whole dialog area (not a blocking zone over buttons)
    this.setSize(dialogW, dialogH);
    this.setInteractive(new Phaser.Geom.Rectangle(0, 0, dialogW, dialogH), Phaser.Geom.Rectangle.Contains);
    this.on('wheel', (_pointer: Phaser.Input.Pointer, _dx: number, _dy: number, dz: number) => {
      if (this.maxScrollY > 0) {
        this.scrollY = Phaser.Math.Clamp(this.scrollY + dz * 0.5, 0, this.maxScrollY);
        this.scrollContainer.y = scrollTop - this.scrollY;
      }
    });

    // Footer: REPAIRS + INSURANCE side by side
    const footerY = scrollTop + visibleHeight + 10;
    this.addFooterButtons(close, events, footerY);
  }

  private buildEventRows(events: OfflineRecapEvent[]): void {
    let y = SCROLL_PADDING;
    for (const evt of events) {
      this.buildRow(evt, y);
      y += ROW_HEIGHT;
    }
    this.contentHeight = y + SCROLL_PADDING;
  }

  private buildRow(evt: OfflineRecapEvent, y: number): void {
    const scene = this.scene;
    const isDestroyed = evt.type === 'destroyed';

    const rowW = this.dw - SCROLL_PADDING * 2;
    const rowH = ROW_HEIGHT - 6;

    // Row background
    const rowBg = scene.add.graphics();
    rowBg.fillStyle(isDestroyed ? 0x2a1010 : 0x1a1a30, 0.6);
    rowBg.fillRoundedRect(SCROLL_PADDING, y, rowW, rowH, Radius.md);
    rowBg.lineStyle(1, isDestroyed ? Colors.red : Colors.gold, 0.15);
    rowBg.strokeRoundedRect(SCROLL_PADDING, y, rowW, rowH, Radius.md);
    this.scrollContainer.add(rowBg);

    const leftX = SCROLL_PADDING + 14;
    const rowCenterY = y + rowH / 2;

    // Ship type + status icon
    const icon = isDestroyed ? '💀' : '⚠';
    const shipLabel = `${icon}  ${evt.unitType}`;
    const shipText = scene.add.text(leftX, rowCenterY - 24, shipLabel, {
      fontSize: FontSize.sm,
      color: isDestroyed ? CSSColors.red : CSSColors.gold,
      fontFamily: Fonts.heading,
    });
    this.scrollContainer.add(shipText);

    // Description line
    const desc = isDestroyed
      ? `Destroyed by ${this.ellipsis(evt.attackerName, 24)}`
      : `Attacked by ${this.ellipsis(evt.attackerName, 24)} — ${evt.totalDamageReceived} dmg`;
    const descText = scene.add.text(leftX, rowCenterY - 5, desc, {
      fontSize: FontSize.xs,
      color: CSSColors.textLight,
      fontFamily: Fonts.body,
    });
    this.scrollContainer.add(descText);

    // Status + location + relative time
    const relTime = this.relativeTime(evt.timestamp);
    let status: string;
    if (isDestroyed) {
      status = `Wreckage at (${evt.position.q}, ${evt.position.r})  •  ${relTime}`;
    } else {
      status = `${evt.currentHp ?? '?'}/${evt.maxHp ?? '?'} HP  •  (${evt.position.q}, ${evt.position.r})  •  ${relTime}`;
    }
    const statusText = scene.add.text(leftX, rowCenterY + 12, status, {
      fontSize: '11px',
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    this.scrollContainer.add(statusText);

    // HP bar for damaged ships
    if (!isDestroyed && evt.currentHp != null && evt.maxHp != null && evt.maxHp > 0) {
      const barX = leftX;
      const barY = rowCenterY + 28;
      const barW = 100;
      const barH = 4;
      const hpRatio = Math.max(0, Math.min(1, evt.currentHp / evt.maxHp));
      const barColor = hpRatio > 0.5 ? 0x44cc44 : hpRatio > 0.2 ? 0xccaa22 : 0xcc4444;

      const barGfx = scene.add.graphics();
      barGfx.fillStyle(0x222222, 0.8);
      barGfx.fillRoundedRect(barX, barY, barW, barH, 2);
      barGfx.fillStyle(barColor, 0.9);
      barGfx.fillRoundedRect(barX, barY, barW * hpRatio, barH, 2);
      this.scrollContainer.add(barGfx);
    }

    // Action button (right side, vertically centered)
    const btnX = this.dw - SCROLL_PADDING - 14 - BTN_W;

    if (isDestroyed) {
      this.addRowButton(btnX, rowCenterY - BTN_H / 2, BTN_W, BTN_H, 'VIEW WRECK', Colors.red, () => {
        EventBus.emit('navigate-to-position', {
          q: evt.position.q, r: evt.position.r, wreckId: evt.wreckId,
        });
        this.destroy();
      });
    } else {
      this.addRowButton(btnX, rowCenterY - BTN_H / 2, BTN_W, BTN_H, 'REPAIR', Colors.green, () => {
        EventBus.emit('navigate-to-position', {
          q: evt.position.q, r: evt.position.r, unitId: evt.unitId,
        });
        this.destroy();
      });
    }
  }

  private addRowButton(
    x: number, y: number, w: number, h: number,
    label: string, color: number, onClick: () => void,
  ): void {
    const scene = this.scene;
    const bg = scene.add.graphics();
    this.drawBtnBg(bg, color, 0.15, 0.4, w, h);
    bg.setPosition(x, y);
    this.scrollContainer.add(bg);

    const text = scene.add.text(x + w / 2, y + h / 2, label, {
      fontSize: FontSize.xs,
      color: `#${color.toString(16).padStart(6, '0')}`,
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    this.scrollContainer.add(text);

    const hitZone = scene.add.zone(x, y, w, h).setOrigin(0, 0);
    hitZone.setInteractive({ useHandCursor: true });
    hitZone.on('pointerover', () => {
      bg.clear();
      this.drawBtnBg(bg, color, 0.3, 0.6, w, h);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      this.drawBtnBg(bg, color, 0.15, 0.4, w, h);
    });
    hitZone.on('pointerdown', onClick);
    this.scrollContainer.add(hitZone);
  }

  private addFooterButtons(
    _onClose: () => void, events: OfflineRecapEvent[], footerY: number,
  ): void {
    const btnH = 36;
    const gap = 16;
    const btnW = (this.dw - SCROLL_PADDING * 2 - gap) / 2;
    const leftX = SCROLL_PADDING;
    const rightX = SCROLL_PADDING + btnW + gap;

    const hasWrecks = events.some(e => e.type === 'destroyed');

    // REPAIRS button (left)
    this.addFooterBtn(leftX, footerY, btnW, btnH, 'REPAIRS', Colors.gold, () => {
      if (hasWrecks) {
        EventBus.emit('open-wreckage-dialog');
      } else {
        const first = events[0];
        EventBus.emit('navigate-to-position', {
          q: first.position.q, r: first.position.r,
          unitId: first.type === 'damaged' ? first.unitId : undefined,
          wreckId: first.type === 'destroyed' ? first.wreckId : undefined,
        });
      }
      this.destroy();
    });

    // INSURANCE button (right)
    this.addFooterBtn(rightX, footerY, btnW, btnH, 'INSURANCE', Colors.cyan, () => {
      EventBus.emit('open-insurance-dialog');
      this.destroy();
    });
  }

  private addFooterBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, onClick: () => void,
  ): void {
    const scene = this.scene;
    const bg = scene.add.graphics();
    bg.setPosition(x, y);
    this.drawBtnBg(bg, color, 0.12, 0.3, w, h);
    this.add(bg);

    const text = scene.add.text(x + w / 2, y + h / 2, label, {
      fontSize: FontSize.sm,
      color: `#${color.toString(16).padStart(6, '0')}`,
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    this.add(text);

    const hitZone = scene.add.zone(x, y, w, h).setOrigin(0, 0);
    hitZone.setInteractive({ useHandCursor: true });
    hitZone.on('pointerover', () => {
      bg.clear();
      this.drawBtnBg(bg, color, 0.25, 0.5, w, h);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      this.drawBtnBg(bg, color, 0.12, 0.3, w, h);
    });
    hitZone.on('pointerdown', onClick);
    this.add(hitZone);
  }

  private drawBtnBg(g: Phaser.GameObjects.Graphics, color: number, fillAlpha: number, strokeAlpha: number, w: number, h: number): void {
    g.fillStyle(color, fillAlpha);
    g.fillRoundedRect(0, 0, w, h, Radius.md);
    g.lineStyle(1, color, strokeAlpha);
    g.strokeRoundedRect(0, 0, w, h, Radius.md);
  }

  private relativeTime(timestamp: string): string {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  private ellipsis(s: string, max: number): string {
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  }

  destroy(fromScene?: boolean): void {
    if (this.scrollMask) this.scrollMask.destroy();
    if (this.maskGraphics) this.maskGraphics.destroy();
    if (this.backdrop) this.backdrop.destroy();
    if (this.backdropZone) this.backdropZone.destroy();
    super.destroy(fromScene);
  }
}
